import Skeleton from 'react-loading-skeleton';
import { AIECGViewer, ECG_VIEWER_CONFIG_STORE } from '@/shared/ui';
import { getSelectedEventType } from '@/entities/event';
import { BEAT_HR_TAB_ID } from '@/entities/report';
import useAfibEcgViewerWrapper from './useAfibEcgViewerWrapper';

const configChart = {
  isDisplayScrollbar: true,
  isEdit: true,
};

export type AfibEcgViewerWrapperProps = {
  ecgDataMap: any;
  profileId: string;
  studyId: string;
  timezoneOffset: number;
  handleClickArrowButton: (isNext: boolean) => void;
};

/**
 * Hosts the ECG viewer for the currently selected AFib event. Shows a three-part skeleton until the ECG object, its samples and the strip start epoch are ready, then mounts the viewer with AFib specific canvas ids, event and bookmark indicators, capture and beat update handlers and the study context.
 *
 * @param props - The viewer props, carrying ecgDataMap, studyId, profileId, timezoneOffset and the arrow button handler.
 * @returns The rendered AFib ECG viewer, or its skeleton while data loads.
 */
const AfibEcgViewerWrapper = ({
  ecgDataMap,
  profileId,
  studyId,
  timezoneOffset,
  handleClickArrowButton,
}: AfibEcgViewerWrapperProps) => {
  const {
    isLoading,
    isLoadingHrChartOptions,
    isLoadingHrChart,
    ecgObject,
    startStripEpoch,
    ecgChartRef,
    handlePickDate,
    pickDateAction,
    eventIndicator,
    bookmarkIndicators,
    getCaptureMode,
    handleCaptureImages,
    clickArrowButtonHandle,
    handleUpdateBeat,
    aiChannel,
    isDisableNextBtn,
    isDisablePrevBtn,
    isPickingDate,
    selectedEvent,
  } = useAfibEcgViewerWrapper({
    ecgDataMap,
    profileId,
    studyId,
    timezoneOffset,
    handleClickArrowButton,
  });

  if (
    isLoading ||
    isLoadingHrChartOptions ||
    isLoadingHrChart ||
    !ecgObject?.dataSamples?.length ||
    !startStripEpoch.current
  ) {
    return (
      <div id="ecg-viewer" className="ecg-viewer-container">
        <div className="ecg-viewer-skeleton-container">
          <div className="scroller-container">
            <Skeleton height="100%" />
          </div>
          <div className="chart-container">
            <Skeleton height="100%" />
          </div>
          <div className="menu-container">
            <Skeleton height="100%" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id="ecg-viewer" className="ecg-viewer-container">
      <AIECGViewer
        ref={ecgChartRef}
        idComponents={{
          canvasBackground: 'cvBgAfibEvents',
          exportGridCanvas: 'expGridCvAfibEvents',
          canvasChart: 'cvChartAfibEvents',
          canvasObject: 'cvObjAfibEvents',
          canvasScroller: 'cvScrollerAfibEvents',
          contextMenu: 'contextMenuAfibEvents',
          tooltip: 'tooltipAfibEvents',
          tab: BEAT_HR_TAB_ID,
        }}
        ecg={ecgObject}
        ecgDataMap={ecgDataMap}
        configChart={configChart}
        isAppliedAI
        isDisableAIAssist
        isHideFineGridMenu
        isHideMarkASampleMenu
        hasArrowButtonScroller
        hasUpdateBeatContextMenu
        handlePickDate={handlePickDate}
        pickDateAction={pickDateAction}
        eventIndicator={eventIndicator}
        bookmarkIndicators={bookmarkIndicators}
        captureMode={getCaptureMode()}
        handleCaptureImages={handleCaptureImages}
        handleClickArrowButton={clickArrowButtonHandle}
        handleUpdateBeat={handleUpdateBeat}
        timeObject={{
          startMoment: startStripEpoch.current,
          timezoneOffset,
        }}
        isInitialARBeatMode={!isPickingDate && ECG_VIEWER_CONFIG_STORE.isInitialARBeatMode}
        aiChannel={aiChannel}
        isDisableNextBtn={isDisableNextBtn}
        isDisablePrevBtn={isDisablePrevBtn}
        studyInfoObject={{
          studyId,
          profileId,
          type: getSelectedEventType(selectedEvent),
        }}
      />
    </div>
  );
};

export default AfibEcgViewerWrapper;
